import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { ScriptShot, ScriptScene, ScriptCharacter, ScriptProp } from '@packages/global';
import { episodeScriptApi } from '../../../services/user/episodeScript.api';

interface ShotCardContentProps {
  shot: ScriptShot;
  index: number;
  scriptId?: string;
  scenes?: ScriptScene[];
  characters?: ScriptCharacter[];
  props?: ScriptProp[];
  onUpdate?: () => void;
  allShots?: ScriptShot[];
}

/**
 * 分镜卡片内容组件
 * 
 * 渲染卡片的主内容区域，保留所有编辑功能
 */
export default function ShotCardContent({
  shot,
  index,
  scriptId,
  scenes = [],
  characters = [],
  props = [],
  onUpdate,
  allShots = []
}: ShotCardContentProps) {
  const { t } = useTranslation(['script', 'common']);
  const [editingField, setEditingField] = useState<'description' | 'dialogue' | 'sceneName' | 'structure' | 'cameraWork' | 'voiceRole' | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingElements, setEditingElements] = useState(false);

  const handleEdit = (field: 'description' | 'dialogue' | 'sceneName' | 'structure' | 'cameraWork' | 'voiceRole', currentValue: string) => {
    setEditingField(field);
    setEditValue(currentValue);
  };

  const handleSave = async () => {
    if (!editingField || !scriptId) return;

    setSaving(true);
    try {
      await episodeScriptApi.updateShot(scriptId, index, {
        [editingField]: editValue
      }, allShots.length > 0 ? allShots : [shot]);
      onUpdate?.();
      setEditingField(null);
    } catch (error) {
      console.error('Failed to update shot:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingField(null);
    setEditValue('');
  };

  const handleElementsUpdate = async (subjects: string[], shotProps: string[]) => {
    if (!scriptId) return;
    setSaving(true);
    try {
      await episodeScriptApi.updateShot(scriptId, index, { subjects, props: shotProps }, allShots.length > 0 ? allShots : [shot]);
      onUpdate?.();
      setEditingElements(false);
    } catch (error) {
      console.error('Failed to update elements:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* 字段列表 */}
      <div className="space-y-2 text-sm">
        {/* 画面描述 */}
        <FieldRow
          label={t('shots.sceneDescription')}
          value={shot.description}
          isEditing={editingField === 'description'}
          editValue={editValue}
          onEdit={() => handleEdit('description', shot.description)}
          onSave={handleSave}
          onCancel={handleCancel}
          onChange={setEditValue}
          saving={saving}
          scriptId={scriptId}
          notSetText={t('shots.notSet')}
          multiline
        />

        {/* 场景名称 */}
        <FieldRow
          label={t('shots.sceneName')}
          value={shot.sceneName || t('shots.notSet')}
          isEditing={editingField === 'sceneName'}
          editValue={editValue}
          onEdit={() => handleEdit('sceneName', shot.sceneName || '')}
          onSave={handleSave}
          onCancel={handleCancel}
          onChange={setEditValue}
          saving={saving}
          scriptId={scriptId}
          notSetText={t('shots.notSet')}
          selectOptions={[
            { value: '', label: t('shots.notSet') },
            ...scenes.map(s => ({ value: s.name, label: s.name }))
          ]}
        />

        {/* 构图设计 */}
        <FieldRow
          label={t('shots.compositionDesign')}
          value={shot.structure || t('shots.notSet')}
          isEditing={editingField === 'structure'}
          editValue={editValue}
          onEdit={() => handleEdit('structure', shot.structure || '')}
          onSave={handleSave}
          onCancel={handleCancel}
          onChange={setEditValue}
          saving={saving}
          scriptId={scriptId}
          notSetText={t('shots.notSet')}
          placeholder={t('shots.compositionPlaceholder')}
        />

        {/* 运镜调度 */}
        <FieldRow
          label={t('shots.cameraWork')}
          value={shot.cameraWork || t('shots.notSet')}
          isEditing={editingField === 'cameraWork'}
          editValue={editValue}
          onEdit={() => handleEdit('cameraWork', shot.cameraWork || '')}
          onSave={handleSave}
          onCancel={handleCancel}
          onChange={setEditValue}
          saving={saving}
          scriptId={scriptId}
          notSetText={t('shots.notSet')}
          placeholder={t('shots.cameraWorkPlaceholder')}
        />

        {/* 配音角色 */}
        <FieldRow
          label={t('shots.voiceRole')}
          value={shot.voiceRole || t('shots.notSet')}
          isEditing={editingField === 'voiceRole'}
          editValue={editValue}
          onEdit={() => handleEdit('voiceRole', shot.voiceRole || '')}
          onSave={handleSave}
          onCancel={handleCancel}
          onChange={setEditValue}
          saving={saving}
          scriptId={scriptId}
          notSetText={t('shots.notSet')}
          selectOptions={[
            { value: '', label: t('shots.notSet') },
            { value: t('shots.narrator'), label: t('shots.narrator') },
            ...characters.map(c => ({ value: c.name, label: c.name }))
          ]}
        />

        {/* 台词内容 */}
        <FieldRow
          label={t('shots.dialogueContent')}
          value={shot.dialogue ? `"${shot.dialogue}"` : t('shots.notSet')}
          isEditing={editingField === 'dialogue'}
          editValue={editValue}
          onEdit={() => handleEdit('dialogue', shot.dialogue || '')}
          onSave={handleSave}
          onCancel={handleCancel}
          onChange={setEditValue}
          saving={saving}
          scriptId={scriptId}
          notSetText={t('shots.notSet')}
          placeholder={t('shots.dialoguePlaceholderContent')}
          multiline
        />

        {/* 出现元素 */}
        <div className="flex items-start gap-2">
          <span className="text-gray-400 whitespace-nowrap min-w-[4rem]">• {t('shots.elements')}</span>
          {editingElements ? (
            <ElementsEditor
              subjects={shot.subjects || []}
              shotProps={shot.props || []}
              availableCharacters={characters}
              availableProps={props}
              onSave={handleElementsUpdate}
              onCancel={() => setEditingElements(false)}
              saving={saving}
            />
          ) : (
            <div className="flex-1 flex items-start gap-2">
              <div className="flex flex-wrap gap-1 flex-1">
                {shot.subjects?.map((subject, i) => (
                  <span key={i} className="px-2 py-1 bg-info/20 text-info text-xs rounded">
                    {subject}
                  </span>
                ))}
                {shot.props?.map((prop, i) => (
                  <span key={i} className="px-2 py-1 bg-success/20 text-success text-xs rounded">
                    {prop}
                  </span>
                ))}
                {(!shot.subjects || shot.subjects.length === 0) && (!shot.props || shot.props.length === 0) && (
                  <span className="text-gray-400 text-xs">{t('shots.none')}</span>
                )}
              </div>
              {scriptId && (
                <button
                  onClick={() => setEditingElements(true)}
                  className="p-1 text-gray-400 hover:text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * 字段行组件 - 统一处理字段的显示和编辑
 */
interface FieldRowProps {
  label: string;
  value: string;
  isEditing: boolean;
  editValue: string;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onChange: (value: string) => void;
  saving: boolean;
  scriptId?: string;
  multiline?: boolean;
  placeholder?: string;
  selectOptions?: { value: string; label: string }[];
  notSetText?: string;
}

function FieldRow({
  label,
  value,
  isEditing,
  editValue,
  onEdit,
  onSave,
  onCancel,
  onChange,
  saving,
  scriptId,
  multiline = false,
  placeholder,
  selectOptions,
  notSetText
}: FieldRowProps) {
  const hasValue = value && value !== (notSetText || '未设置');

  return (
    <div className="flex items-start gap-2">
      <span className="text-gray-400 whitespace-nowrap min-w-[4rem]">• {label}</span>
      {isEditing ? (
        <div className="flex-1 flex gap-2">
          {selectOptions ? (
            <select
              value={editValue}
              onChange={(e) => onChange(e.target.value)}
              className="flex-1 bg-bg-form text-gray-200 rounded px-2 py-1 text-sm border border-border-subtle"
              disabled={saving}
            >
              {selectOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          ) : multiline ? (
            <textarea
              value={editValue}
              onChange={(e) => onChange(e.target.value)}
              className="flex-1 bg-bg-form text-gray-200 rounded px-2 py-1 text-sm resize-none border border-border-subtle"
              rows={2}
              disabled={saving}
              placeholder={placeholder}
            />
          ) : (
            <input
              value={editValue}
              onChange={(e) => onChange(e.target.value)}
              className="flex-1 bg-bg-form text-gray-200 rounded px-2 py-1 text-sm border border-border-subtle"
              disabled={saving}
              placeholder={placeholder}
            />
          )}
          <div className="flex gap-1">
            <button
              onClick={onSave}
              disabled={saving}
              className="p-1 text-green-400 hover:text-green-300 disabled:opacity-50"
            >
              <CheckIcon className="w-4 h-4" />
            </button>
            <button
              onClick={onCancel}
              disabled={saving}
              className="p-1 text-red-400 hover:text-red-300 disabled:opacity-50"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-start gap-2">
          <span className={`flex-1 ${hasValue ? 'text-gray-200' : 'text-gray-500'}`}>
            {value}
          </span>
          {scriptId && (
            <button
              onClick={onEdit}
              className="p-1 text-gray-400 hover:text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * 出现元素编辑器组件
 */
interface ElementsEditorProps {
  subjects: string[];
  shotProps: string[];
  availableCharacters: { name: string }[];
  availableProps: { name: string }[];
  onSave: (subjects: string[], shotProps: string[]) => void;
  onCancel: () => void;
  saving: boolean;
}

function ElementsEditor({
  subjects,
  shotProps,
  availableCharacters,
  availableProps,
  onSave,
  onCancel,
  saving
}: ElementsEditorProps) {
  const { t } = useTranslation(['script', 'common']);
  const [localSubjects, setLocalSubjects] = useState<string[]>(subjects);
  const [localProps, setLocalProps] = useState<string[]>(shotProps);

  const addSubject = (name: string) => {
    if (!localSubjects.includes(name)) {
      setLocalSubjects([...localSubjects, name]);
    }
  };

  const removeSubject = (index: number) => {
    setLocalSubjects(localSubjects.filter((_, i) => i !== index));
  };

  const addProp = (name: string) => {
    if (!localProps.includes(name)) {
      setLocalProps([...localProps, name]);
    }
  };

  const removeProp = (index: number) => {
    setLocalProps(localProps.filter((_, i) => i !== index));
  };

  return (
    <div className="flex-1 flex flex-col gap-2">
      <div className="space-y-2">
        <div className="flex flex-wrap gap-1">
          {localSubjects.map((subject, i) => (
            <span key={i} className="px-2 py-1 bg-info/20 text-info text-xs rounded flex items-center gap-1">
              {subject}
              <button onClick={() => removeSubject(i)} className="text-info/70 hover:text-info cursor-pointer">
                <XMarkIcon className="w-3 h-3" />
              </button>
            </span>
          ))}
          {localProps.map((prop, i) => (
            <span key={i} className="px-2 py-1 bg-success/20 text-success text-xs rounded flex items-center gap-1">
              {prop}
              <button onClick={() => removeProp(i)} className="text-success/70 hover:text-success cursor-pointer">
                <XMarkIcon className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <select
            onChange={(e) => { if (e.target.value) { addSubject(e.target.value); e.target.value = ''; } }}
            className="flex-1 min-w-0 bg-bg-form text-gray-200 rounded px-2 py-1 text-xs border border-border-subtle"
            disabled={saving}
          >
            <option value="">{t('shots.addCharacter')}</option>
            {availableCharacters.filter(c => !localSubjects.includes(c.name)).map((char) => (
              <option key={char.name} value={char.name}>{char.name}</option>
            ))}
          </select>
          <select
            onChange={(e) => { if (e.target.value) { addProp(e.target.value); e.target.value = ''; } }}
            className="flex-1 min-w-0 bg-bg-form text-gray-200 rounded px-2 py-1 text-xs border border-border-subtle"
            disabled={saving}
          >
            <option value="">{t('shots.addProp')}</option>
            {availableProps.filter(p => !localProps.includes(p.name)).map((prop) => (
              <option key={prop.name} value={prop.name}>{prop.name}</option>
            ))}
          </select>
        </div>
        <div className="flex justify-end gap-1">
          <button
            onClick={() => onSave(localSubjects, localProps)}
            disabled={saving}
            className="p-1 text-green-400 hover:text-green-300 disabled:opacity-50 cursor-pointer"
          >
            <CheckIcon className="w-4 h-4" />
          </button>
          <button
            onClick={onCancel}
            disabled={saving}
            className="p-1 text-red-400 hover:text-red-300 disabled:opacity-50 cursor-pointer"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
